import React, { useState } from 'react';
import {
  Edit,
  Trash2,
  MoreVertical,
  TrendingUp,
  DollarSign,
} from 'lucide-react';
import { EditAmplifyPOCDialog } from './EditAmplifyPOCDialog';
import { DeleteAmplifyPOCDialog } from './DeleteAmplifyPOCDialog';
import { POCFilterBar, POCPaginationControls } from './POCFiltersAndPagination';

/**
 * Amplify POC Table Component
 * Displays and manages Amplify POC locations with budgets
 */
export function AmplifyPOCTable({ pocs, totalPOCs, filters, isLoading, onFilterChange, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [selectedRegion, setSelectedRegion] = useState(filters.region || '');
  const [selectedCountry, setSelectedCountry] = useState(filters.country || '');
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [editingPOC, setEditingPOC] = useState(null);
  const [deletingPOC, setDeletingPOC] = useState(null);

  // Get unique regions and countries for filters (compare function for reliable string sort)
  const stringCompare = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
  const regions = [...new Set(pocs.map(poc => poc.region).filter(Boolean))].sort(stringCompare);
  const countries = [...new Set(pocs.map(poc => poc.country).filter(Boolean))].sort(stringCompare);

  const handleSearch = (value) => {
    setSearchTerm(value);
    onFilterChange({ search: value, page: 1 });
  };

  const handleRegionFilter = (value) => {
    setSelectedRegion(value);
    onFilterChange({ region: value, page: 1 });
  };

  const handleCountryFilter = (value) => {
    setSelectedCountry(value);
    onFilterChange({ country: value, page: 1 });
  };

  const handlePageChange = (newPage) => {
    onFilterChange({ page: newPage });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="space-y-4">
      <POCFilterBar
        searchTerm={searchTerm}
        selectedRegion={selectedRegion}
        selectedCountry={selectedCountry}
        regions={regions}
        countries={countries}
        onSearch={handleSearch}
        onRegionChange={handleRegionFilter}
        onCountryChange={handleCountryFilter}
      />

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                POC ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Location
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Region
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Country
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Traditional Budget
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
                  </div>
                </td>
              </tr>
            ) : pocs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                  <TrendingUp className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No Amplify POCs found</p>
                  {(searchTerm || selectedRegion || selectedCountry) && (
                    <p className="text-xs mt-1">Try adjusting your filters</p>
                  )}
                </td>
              </tr>
            ) : (
              pocs.map((poc) => (
                <tr key={poc.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    {poc.id}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {poc.poc_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex flex-col">
                      {poc.address && poc.address !== 'Unknown' && <span>{poc.address}</span>}
                      <span>{poc.city}{poc.state && poc.state !== 'Unknown' ? `, ${poc.state}` : ''}</span>
                      {poc.zipcode && poc.zipcode !== 'Unknown' && (
                        <span className="text-xs">{poc.zipcode}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {poc.region || 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {poc.country}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <DollarSign className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(poc.traditional_budget)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setActionMenuOpen(actionMenuOpen === poc.id ? null : poc.id)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        <MoreVertical className="h-4 w-4 text-gray-400" />
                      </button>
                      {actionMenuOpen === poc.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                          <button
                            onClick={() => {
                              setEditingPOC(poc);
                              setActionMenuOpen(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Edit className="h-4 w-4" />
                            Edit Amplify POC
                          </button>
                          <button
                            onClick={() => {
                              setDeletingPOC(poc);
                              setActionMenuOpen(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete Amplify POC
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <POCPaginationControls
        totalPOCs={totalPOCs}
        filters={filters}
        onPageChange={handlePageChange}
      />

      {/* Dialogs */}
      {editingPOC && (
        <EditAmplifyPOCDialog
          poc={editingPOC}
          isOpen={!!editingPOC}
          onClose={() => setEditingPOC(null)}
          onSuccess={() => {
            setEditingPOC(null);
            onRefresh();
          }}
        />
      )}

      {deletingPOC && (
        <DeleteAmplifyPOCDialog
          poc={deletingPOC}
          isOpen={!!deletingPOC}
          onClose={() => setDeletingPOC(null)}
          onSuccess={() => {
            setDeletingPOC(null);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

export default AmplifyPOCTable;
